// src/storage/useUserContent.js
//
// Auth-bound hooks for USER-OWNED bookmarks + notes (Doc #2 V1.2). These are the first
// client-writable owner docs: /users/{uid}/bookmarks/{id} + /users/{uid}/notes/{id},
// docId = `${packId}__${itemId}` (one per content item). Rules enforce owner-only + the
// docId binding + serverTimestamp() on createdAt/updatedAt -- so all writes here MUST use
// serverTimestamp() (see firestore.rules validBookmark/validNote).
//
// Mirrors useEntitlements: onSnapshot rebuilt on uid change, torn down on sign-out; mock /
// signed-out / no-db returns an empty, non-loading result so anonymous browse still works.
import { useState, useEffect, useCallback } from 'react';
import { collection, doc, onSnapshot, setDoc, deleteDoc, serverTimestamp } from 'firebase/firestore';
import { db, AUTH_MODE } from '../auth/firebase.js';
import { useAuth } from '../auth/useAuth.js';

// Deterministic docId. packId is a strict slug (no '_'); itemId may contain '_' (real ids
// are 'item_<hex>'). The '__' join is unambiguous because packId never contains '__'.
export function userContentDocId(packId, itemId) {
    return `${packId}__${itemId}`;
}

const disabled = () => AUTH_MODE === 'mock' || !db;

// ---- Bookmarks: live set for the signed-in user + toggle ----
export function useBookmarks() {
    const { currentUser } = useAuth();
    const uid = currentUser?.uid || null;
    const [bookmarks, setBookmarks] = useState([]); // [{ id, packId, itemId, itemType, title }]
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (disabled() || !uid) { setBookmarks([]); setLoading(false); return undefined; }
        setLoading(true);
        const ref = collection(db, 'users', uid, 'bookmarks');
        const unsub = onSnapshot(
            ref,
            (snap) => {
                const list = [];
                snap.forEach((d) => list.push({ id: d.id, ...d.data() }));
                setBookmarks(list);
                setLoading(false);
            },
            (err) => { console.error('useBookmarks listener error:', err); setBookmarks([]); setLoading(false); },
        );
        return () => unsub();
    }, [uid]);

    const bookmarkIds = new Set(bookmarks.map((b) => b.id));

    // Toggle: create when absent, delete when present. Caller passes the denormalized meta.
    const toggleBookmark = useCallback(async ({ packId, itemId, itemType = null, title = null }) => {
        if (disabled() || !uid || !packId || !itemId) return;
        const id = userContentDocId(packId, itemId);
        const ref = doc(db, 'users', uid, 'bookmarks', id);
        try {
            if (bookmarkIds.has(id)) {
                await deleteDoc(ref);
            } else {
                await setDoc(ref, { packId, itemId, itemType, title, createdAt: serverTimestamp() });
            }
        } catch (e) {
            console.error('toggleBookmark write error:', e);
        }
    }, [uid, bookmarks]); // bookmarks dep keeps bookmarkIds fresh inside the callback

    return { bookmarks, bookmarkIds, loading, uid, toggleBookmark };
}

// ---- Note: the single note doc for one (packId,itemId) + save/clear ----
export function useNote(packId, itemId) {
    const { currentUser } = useAuth();
    const uid = currentUser?.uid || null;
    const [note, setNote] = useState(null);   // { createdAt, ... } | null
    const [loading, setLoading] = useState(true);

    const enabled = !disabled() && uid && packId && itemId;
    const id = (packId && itemId) ? userContentDocId(packId, itemId) : null;

    useEffect(() => {
        if (!enabled) { setNote(null); setLoading(false); return undefined; }
        setLoading(true);
        const ref = doc(db, 'users', uid, 'notes', id);
        const unsub = onSnapshot(
            ref,
            (snap) => { setNote(snap.exists() ? { id: snap.id, ...snap.data() } : null); setLoading(false); },
            (err) => { console.error('useNote listener error:', err); setNote(null); setLoading(false); },
        );
        return () => unsub();
    }, [uid, id, enabled]);

    // Save (create or update). On UPDATE the rules require createdAt unchanged, so we send
    // back the loaded createdAt Timestamp; on CREATE we stamp it with serverTimestamp().
    const saveNote = useCallback(async (body, { itemType = null, title = null } = {}) => {
        if (!enabled) return;
        const ref = doc(db, 'users', uid, 'notes', id);
        const createdAt = note && note.createdAt ? note.createdAt : serverTimestamp();
        try {
            await setDoc(ref, { packId, itemId, itemType, title, body: body || '', createdAt, updatedAt: serverTimestamp() });
        } catch (e) {
            console.error('saveNote write error:', e);
            throw e;
        }
    }, [enabled, uid, id, packId, itemId, note]);

    const clearNote = useCallback(async () => {
        if (!enabled) return;
        try { await deleteDoc(doc(db, 'users', uid, 'notes', id)); }
        catch (e) { console.error('clearNote write error:', e); throw e; }
    }, [enabled, uid, id]);

    return { note, loading, uid: enabled ? uid : null, saveNote, clearNote };
}

// ---- All notes: live list for the signed-in user (Account "My Notes"). Read-only
// here -- editing/clearing stays in the reader's notes drawer (useNote). Mirrors
// useBookmarks (onSnapshot over the collection; empty + non-loading when signed-out). ----
export function useAllNotes() {
    const { currentUser } = useAuth();
    const uid = currentUser?.uid || null;
    const [notes, setNotes] = useState([]); // [{ id, packId, itemId, itemType, title, body, createdAt, updatedAt }]
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (disabled() || !uid) { setNotes([]); setLoading(false); return undefined; }
        setLoading(true);
        const ref = collection(db, 'users', uid, 'notes');
        const unsub = onSnapshot(
            ref,
            (snap) => {
                const list = [];
                snap.forEach((d) => list.push({ id: d.id, ...d.data() }));
                setNotes(list);
                setLoading(false);
            },
            (err) => { console.error('useAllNotes listener error:', err); setNotes([]); setLoading(false); },
        );
        return () => unsub();
    }, [uid]);

    return { notes, loading };
}
